import { useCallback, useEffect, useState } from 'react';
import { deriveProjectBadgeChars, getRandomProjectBadgeColor } from '@/shared/data/projectBadges';
import type { useBootstrapData } from './useBootstrapData';
import type { AuthSession } from '@/auth/types';
import type { ProjectDto } from '@flowqa/shared';

type UseAppDialogsArgs = {
  session: AuthSession | null;
  bootstrap: ReturnType<typeof useBootstrapData>;
  navigate: (path: string) => void;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  updateUserContext: (input: {
    lastOrganisationId?: string | null;
    lastProjectId?: string | null;
    onboardingCompletedAt?: string | null;
  }) => Promise<{
    lastOrganisationId: string | null;
    lastProjectId: string | null;
    onboardingCompletedAt: string | null;
  }>;
};

type ProjectFormState = {
  name: string;
  badgeEnabled: boolean;
  badgeChars: string;
  badgeColor: ProjectDto['badgeColor'];
};
type OpenAddProjectOptions = {
  onboarding?: boolean;
};

const createInitialProjectFormState = (): ProjectFormState => ({
  name: '',
  badgeEnabled: true,
  badgeChars: deriveProjectBadgeChars(''),
  badgeColor: getRandomProjectBadgeColor(),
});

export const useAppDialogs = ({
  session,
  bootstrap,
  navigate,
  refreshSession,
  setSessionDirect,
  updateUserContext,
}: UseAppDialogsArgs): {
  addProjectDialog: {
    open: boolean;
    saving: boolean;
    onboarding: boolean;
    name: string;
    badgeEnabled: boolean;
    badgeChars: string;
    badgeColor: ProjectDto['badgeColor'];
    errorMessage: string | null;
    onNameChange: (value: string) => void;
    onBadgeEnabledChange: (value: boolean) => void;
    onBadgeCharsChange: (value: string) => void;
    onBadgeColorChange: (value: ProjectDto['badgeColor']) => void;
    onClose: () => void;
    onSave: () => Promise<void>;
  };
  addOrganisationDialog: {
    open: boolean;
    saving: boolean;
    name: string;
    errorMessage: string | null;
    onNameChange: (value: string) => void;
    onClose: () => void;
    onSave: () => Promise<void>;
  };
  openAddProject: (setAsCurrent: boolean, options?: OpenAddProjectOptions) => void;
  openAddOrganisation: () => void;
  dismissAll: () => void;
} => {
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [addProjectSetContext, setAddProjectSetContext] = useState(true);
  const [addProjectOnboarding, setAddProjectOnboarding] = useState(false);
  const [addProjectLoading, setAddProjectLoading] = useState(false);
  const [addProjectError, setAddProjectError] = useState<string | null>(null);
  const [addProjectBadgeTouched, setAddProjectBadgeTouched] = useState(false);
  const [newProject, setNewProject] = useState<ProjectFormState>(createInitialProjectFormState);
  const [addOrgOpen, setAddOrgOpen] = useState(false);
  const [addOrgLoading, setAddOrgLoading] = useState(false);
  const [addOrgError, setAddOrgError] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState('');

  const resetAddProjectForm = useCallback(() => {
    setNewProject(createInitialProjectFormState());
    setAddProjectBadgeTouched(false);
    setAddProjectError(null);
  }, []);

  const resetAddOrgForm = useCallback(() => {
    setNewOrgName('');
    setAddOrgError(null);
  }, []);

  const handleAddProject = useCallback(async (): Promise<void> => {
    if (!session?.tokens.accessToken) return;
    if (newProject.name.trim().length === 0) {
      setAddProjectError('Please fill in the required fields.');
      return;
    }
    setAddProjectLoading(true);
    setAddProjectError(null);
    try {
      const created = await bootstrap.createProject(newProject, {
        setAsCurrent: addProjectSetContext,
      });
      if (created && addProjectOnboarding) {
        const completionTimestamp = new Date().toISOString();
        const updatedContext = await updateUserContext({
          lastOrganisationId: created.organisationId ?? null,
          lastProjectId: created.id,
          onboardingCompletedAt: completionTimestamp,
        });
        setSessionDirect({
          ...session,
          lastOrganisationId: updatedContext.lastOrganisationId ?? created.organisationId ?? null,
          lastProjectId: updatedContext.lastProjectId ?? created.id,
          onboardingCompleted: true,
        });
      }
      setAddProjectOpen(false);
      setAddProjectOnboarding(false);
      resetAddProjectForm();
      if (created) {
        navigate('/execution-runs');
        await refreshSession();
      }
    } catch (err) {
      setAddProjectError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddProjectLoading(false);
    }
  }, [
    addProjectSetContext,
    addProjectOnboarding,
    bootstrap,
    navigate,
    newProject,
    refreshSession,
    resetAddProjectForm,
    session,
    session?.tokens.accessToken,
    setSessionDirect,
    updateUserContext,
  ]);

  const handleAddOrganisation = useCallback(async (): Promise<void> => {
    if (!session?.tokens.accessToken) return;
    if (newOrgName.trim().length === 0) {
      setAddOrgError('Please fill in the required fields.');
      return;
    }
    setAddOrgLoading(true);
    setAddOrgError(null);
    try {
      const created = await bootstrap.createOrg(newOrgName);
      setAddOrgOpen(false);
      resetAddOrgForm();
      if (created) {
        navigate('/execution-runs');
        await refreshSession();
      }
    } catch (err) {
      setAddOrgError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddOrgLoading(false);
    }
  }, [bootstrap, navigate, newOrgName, refreshSession, resetAddOrgForm, session]);

  const openAddProject = useCallback((setAsCurrent: boolean, options?: OpenAddProjectOptions) => {
    setAddProjectSetContext(setAsCurrent);
    setAddProjectOnboarding(options?.onboarding ?? false);
    setAddProjectOpen(true);
  }, []);

  const openAddOrganisation = useCallback(() => {
    setAddOrgOpen(true);
    setAddOrgError(null);
    setNewOrgName('');
  }, []);

  const dismissAll = useCallback(() => {
    setAddProjectOpen(false);
    setAddProjectOnboarding(false);
    setAddProjectLoading(false);
    setAddProjectError(null);
    setNewProject(createInitialProjectFormState());
    setAddProjectBadgeTouched(false);
    setAddOrgOpen(false);
    setAddOrgLoading(false);
    setAddOrgError(null);
    setNewOrgName('');
  }, []);

  useEffect(() => {
    if (session) return;
    dismissAll();
  }, [dismissAll, session]);

  return {
    addProjectDialog: {
      open: addProjectOpen,
      saving: addProjectLoading,
      onboarding: addProjectOnboarding,
      name: newProject.name,
      badgeEnabled: newProject.badgeEnabled,
      badgeChars: newProject.badgeChars,
      badgeColor: newProject.badgeColor,
      errorMessage: addProjectError,
      onNameChange: (value: string) => {
        setNewProject((prev) => ({
          ...prev,
          name: value,
          ...(prev.badgeEnabled && !addProjectBadgeTouched
            ? { badgeChars: deriveProjectBadgeChars(value) }
            : {}),
        }));
        setAddProjectError(null);
      },
      onBadgeEnabledChange: (value: boolean) => {
        setNewProject((prev) => ({
          ...prev,
          badgeEnabled: value,
          ...(!addProjectBadgeTouched && value
            ? { badgeChars: deriveProjectBadgeChars(prev.name) }
            : {}),
        }));
        setAddProjectError(null);
      },
      onBadgeCharsChange: (value: string) => {
        setAddProjectBadgeTouched(true);
        setNewProject((prev) => ({ ...prev, badgeChars: value }));
        setAddProjectError(null);
      },
      onBadgeColorChange: (value: ProjectDto['badgeColor']) => {
        setAddProjectBadgeTouched(true);
        setNewProject((prev) => ({ ...prev, badgeColor: value }));
        setAddProjectError(null);
      },
      onClose: () => {
        if (addProjectOnboarding) {
          return;
        }
        setAddProjectOpen(false);
        setAddProjectOnboarding(false);
        resetAddProjectForm();
      },
      onSave: handleAddProject,
    },
    addOrganisationDialog: {
      open: addOrgOpen,
      saving: addOrgLoading,
      name: newOrgName,
      errorMessage: addOrgError,
      onNameChange: (value: string) => {
        setNewOrgName(value);
        setAddOrgError(null);
      },
      onClose: () => {
        setAddOrgOpen(false);
        resetAddOrgForm();
      },
      onSave: handleAddOrganisation,
    },
    openAddProject,
    openAddOrganisation,
    dismissAll,
  };
};
